import React from 'react';

import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import type { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { FAQTab } from '../../../../common/display/faqTab/FAQTab';
import { useGetHasProduct } from '../../../../../customHooks/useGetHasProduct.hook';
import { ProductType } from '../../../../../@types/product';
import { useGetDiscoverKaeuferPlusLink } from '../../../../../customHooks/useGetDiscoverKaeuferPlusLink.hook';
import { Msg, useIntl } from '@/i18n';
import ExclusiveListingsJpg from '@/components/content/benefits/modals/exclusiveListings/exclusive-listings.jpg';
import ExclusiveListingsWebp from '@/components/content/benefits/modals/exclusiveListings/exclusive-listings.webp';
import ExclusiveListingsMobileJpg from '@/components/content/benefits/modals/exclusiveListings/exclusive-listings_mobile.jpg';
import ExclusiveListingsMobileWebp from '@/components/content/benefits/modals/exclusiveListings/exclusive-listings_mobile.webp';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewTab: { maxWidth: 530 },
};

const OverviewTab = ({ hasKaeuferPlus }: { hasKaeuferPlus: boolean }) => {
  return (
    <div style={styles.overviewTab}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.exclusiveListings.modal.overview.paragraph1'
          defaultMessage='"Exclusive listings" come from...'
        />
      </div>
      <div className='padding-bottom-l'>
        <Msg
          id='benefits.exclusiveListings.modal.overview.paragraph2'
          defaultMessage='The exclusive listings appear for you...'
        />
      </div>
      {!hasKaeuferPlus && (
        <ul className='list-check list-spacing-s'>
          <li data-testid='kaeufer-plus-label-with-checkmark'>
            <Msg
              id='benefits.exclusiveListings.modal.overview.activeKaeuferPlus'
              defaultMessage='Keep Käufer Plus...'
              values={{
                BuyerPlusLogo: <PlusBadge variant='buy' />,
              }}
            />
          </li>
        </ul>
      )}
    </div>
  );
};

const ButtonArea = () => {
  const discoverKaeuferPlusLink = useGetDiscoverKaeuferPlusLink({ ctaType: 'exclusiveListings' });

  return (
    <div className='align-center'>
      <a className='button-secondary button-rounded' href={discoverKaeuferPlusLink}>
        <Msg id='benefits.exclusiveListings.modal.linkButton' defaultMessage='Discover KäuferPlus' />
      </a>
    </div>
  );
};

export const ExclusiveListings = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const hasKaeuferPlus = useGetHasProduct(ProductType.KaeuferPlus);

  return (
    <Modal
      title={t({ id: 'benefits.exclusiveListings.headline', defaultMessage: 'Exclusive listings' })}
      isVisible={isVisible}
      image={ExclusiveListingsJpg}
      imageWebp={ExclusiveListingsWebp}
      imageMobile={ExclusiveListingsMobileJpg}
      imageMobileWebp={ExclusiveListingsMobileWebp}
      imageUseFullWidth={false}
      imageAlt={t({ id: 'benefits.exclusiveListings.modal.image', defaultMessage: 'Phone with preview' })}
      tabsContent={[
        <OverviewTab key='benefits.exclusiveListings.modal.overview' hasKaeuferPlus={hasKaeuferPlus} />,
        <FAQTab key='benefits.exclusiveListings.modal.faq' benefit='exclusiveListings' />,
      ]}
      benefit={Benefit.EXCLUSIVE_LISTINGS}
      buttonArea={hasKaeuferPlus ? null : <ButtonArea />}
      onClose={onClose}
    />
  );
};
